import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useAppKitAccount } from '@reown/appkit/react';
import { useState, useEffect } from 'react';

import { useUserLotteryNFTs } from '../../hooks/usePointsLottery';

import config from '@/config';
import type { NFTTier } from '@/types/schemas';

// Helper function to get correct NFT image based on tier
// This ensures tier metadata and image are always in sync
function getTierImageUrl(tier: NFTTier): string {
  const tierMap = {
    diamond: '/nfts/DiamondTierNFT_JunkFun.png',
    black: '/nfts/BlackTierNFT_JunkFun.png',
    gold: '/nfts/GoldTierNFT_JunkFun.png',
    silver: '/nfts/SilverTierNFT_JunkFun.png',
    bronze: '/nfts/BronzeTierNFT_JunkFun.png',
  };
  return tierMap[tier] || '/nfts/BronzeTierNFT_JunkFun.png'; // Fallback to bronze
}

// Add keyframe animation styles
const floatInAnimation = `
  @keyframes floatIn {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Helper to determine if we're on devnet based on RPC URL
const isDevnet = () => {
  const rpcUrl = config.SOLANA_RPC_URL || '';
  return rpcUrl.includes('devnet');
};

// Helper to generate Solscan URL with appropriate cluster
const getSolscanUrl = (mintAddress: string) => {
  const baseUrl = `https://solscan.io/token/${mintAddress}`;
  return isDevnet() ? `${baseUrl}?cluster=devnet` : baseUrl;
};

// NFT Image component with tier-based image selection
const NFTImage = ({ nft }: { nft: any }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Use tier-based local image instead of backend imageUrl
  // This fixes the metadata/image mismatch where tier is correct but imageUrl is wrong
  const currentImageUrl = getTierImageUrl(nft.tier);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  // Show tier-based image (always available as local asset)
  if (!imageError) {
    return (
      <div className="relative w-full h-full">
        {imageLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#50E4F9]"></div>
          </div>
        )}
        <img
          src={currentImageUrl}
          alt={nft.name || `Season ${nft.seasonNo} NFT`}
          className="w-full h-full object-cover"
          onLoad={handleImageLoad}
          onError={handleImageError}
        />

        {/* Rank Badge - Top Left */}
        {nft.rank && (
          <div className="absolute top-2 left-2 bg-[#50E4F9]/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg border border-[#50E4F9]/50">
            <span className="text-sm font-bold text-[#011824]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              #{nft.rank}
            </span>
          </div>
        )}

        {/* Season Badge - Top Right */}
        <div className="absolute top-2 right-2 bg-[#50E4F9]/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg border border-[#50E4F9]/50">
          <span className="text-sm font-bold text-[#011824]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            S{nft.seasonNo}
          </span>
        </div>
      </div>
    );
  }

  // Fallback to tier text display
  return (
    <div className="relative w-full h-full flex items-center justify-center p-8">
      <div className="text-center">
        <div
          className="text-6xl font-bold mb-2"
          style={{
            fontFamily: 'Hyperspace Race Capsule, cursive',
            background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {nft.tier ? nft.tier.toUpperCase() : 'NFT'}
        </div>
        {nft.rank && (
          <div className="text-2xl font-bold text-white">
            #{nft.rank}
          </div>
        )}
      </div>

      {/* Rank Badge - Top Left */}
      {nft.rank && (
        <div className="absolute top-2 left-2 bg-[#50E4F9]/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg border border-[#50E4F9]/50">
          <span className="text-sm font-bold text-[#011824]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            #{nft.rank}
          </span>
        </div>
      )}

      {/* Season Badge - Top Right */}
      <div className="absolute top-2 right-2 bg-[#50E4F9]/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg border border-[#50E4F9]/50">
        <span className="text-sm font-bold text-[#011824]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          S{nft.seasonNo}
        </span>
      </div>
    </div>
  );
};

const MyNFT = () => {
  const navigate = useNavigate();
  const { isConnected } = useAppKitAccount();
  const { data: nftsData, isLoading: nftsLoading, error: nftsError } = useUserLotteryNFTs();

  const userNFTs = nftsData || [];

  // Inject animation styles
  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = floatInAnimation;
    document.head.appendChild(styleTag);
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden" style={{ backgroundColor: '#011824', fontFamily: 'Outfit, sans-serif' }}>
      <div className="h-full overflow-y-auto">
        <div className="p-4">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center mb-8">
            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-[#50E4F9]/10 transition-all duration-300 hover:scale-110 border border-transparent hover:border-[#50E4F9]/30"
            >
              <ArrowLeft size={24} className="text-[#50E4F9]" />
            </button>
          </div>

          {/* Page Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-8">
            <span
              style={{
                fontFamily: 'Hyperspace Race Capsule, cursive',
                background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              My NFTs
            </span>
          </h1>

          {/* Content */}
          {!isConnected ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-4 text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Connect Your Wallet</h2>
                <p className="text-[#50E4F9]/70" style={{ fontFamily: 'Outfit, sans-serif' }}>Connect your wallet to view your NFT collection</p>
              </div>
            </div>
          ) : nftsLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#50E4F9]"></div>
              <p className="text-[#50E4F9]/70 mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Loading your NFTs...</p>
            </div>
          ) : nftsError ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-4 text-red-400" style={{ fontFamily: 'Outfit, sans-serif' }}>Unable to Load NFTs</h2>
                <p className="text-[#50E4F9]/70 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  There was an error loading your NFTs. Please ensure you are signed in.
                </p>
                <p className="text-[#50E4F9]/50 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  If you recently connected your wallet, try refreshing the page.
                </p>
              </div>
            </div>
          ) : userNFTs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-6">
              <div className="relative w-full max-w-md flex items-center justify-center">
                <img
                  src="/winner.svg"
                  alt="Trophy"
                  className="w-full h-auto object-contain"
                  style={{
                    filter: 'drop-shadow(0 0 20px rgba(80, 228, 249, 0.3))',
                  }}
                />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-4 text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>No NFTs Yet</h2>
                <p className="text-[#50E4F9]/70" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Participate in Points Lottery seasons to earn exclusive NFTs!
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-8">
              {userNFTs.map((nft, index) => (
                <div
                  key={`${nft.mintAddress || nft.id}-${index}`}
                  className="bg-gradient-to-b from-[#0a2432] to-[#011824] rounded-xl overflow-hidden border border-[#50E4F9]/20 hover:border-[#50E4F9] transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#50E4F9]/20 group"
                  style={{
                    animation: 'floatIn 0.6s ease-out forwards',
                    animationDelay: `${index * 0.1}s`,
                    opacity: 0,
                  }}
                >
                  {/* NFT Image */}
                  <div className="aspect-square bg-gradient-to-br from-[#0a2432] to-[#011824] overflow-hidden relative">
                    <NFTImage nft={nft} />
                  </div>

                  {/* NFT Info */}
                  <div className="p-4 space-y-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <h3 className="font-bold text-white text-lg group-hover:text-[#50E4F9] transition-colors duration-300">
                      {nft.name || `Season ${nft.seasonNo} NFT`}
                    </h3>
                    {nft.description && (
                      <p className="text-sm text-gray-300" title={nft.description}>
                        {nft.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[#50E4F9]">Tier:</span>
                      <span className="text-sm text-white font-medium">
                        {nft.tier.charAt(0).toUpperCase() + nft.tier.slice(1)}
                      </span>
                    </div>
                    {nft.mintAddress && (
                      <p className="text-xs text-[#50E4F9]/50 truncate font-mono" title={nft.mintAddress}>
                        {nft.mintAddress.slice(0, 8)}...{nft.mintAddress.slice(-8)}
                      </p>
                    )}
                  </div>

                  {/* View on Explorer Button */}
                  {nft.mintAddress && (
                    <div className="p-4 pt-0">
                      <a
                        href={getSolscanUrl(nft.mintAddress)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full py-2.5 px-4 bg-[#50E4F9]/10 hover:bg-[#50E4F9]/20 text-[#50E4F9] text-center rounded-lg text-sm font-medium transition-all duration-300 border border-[#50E4F9]/30 hover:border-[#50E4F9] hover:shadow-lg hover:shadow-[#50E4F9]/20"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        View on Solscan
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyNFT;
